// Widget Theme Configuration - Elfsight-inspired design system

export const widgetTheme = {
  // Color palette
  colors: {
    primary: '#6B9F4B', // Default green - will be overridden by client config
    white: '#FFFFFF',
    gray: {
      50: '#F9FAFB',
      100: '#F3F4F6',
      200: '#E5E7EB',
      300: '#D1D5DB',
      400: '#9CA3AF',
      500: '#6B7280',
      600: '#4B5563',
      700: '#374151',
      800: '#1F2937',
      900: '#111827',
    },
  },

  // Typography scale
  typography: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    sizes: {
      xs: '0.75rem',    // 12px
      sm: '0.875rem',   // 14px
      base: '1rem',     // 16px
      lg: '1.125rem',   // 18px
      xl: '1.25rem',    // 20px
      '2xl': '1.5rem',  // 24px
    },
    weights: {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
  },

  // Spacing scale
  spacing: {
    xs: '0.25rem',   // 4px
    sm: '0.5rem',    // 8px
    md: '0.75rem',   // 12px
    lg: '1rem',      // 16px
    xl: '1.25rem',   // 20px
    '2xl': '1.5rem', // 24px
    '3xl': '2rem',   // 32px
  },

  // Border radius
  radius: {
    sm: '0.375rem',  // 6px
    md: '0.5rem',    // 8px
    lg: '0.75rem',   // 12px
    xl: '1rem',      // 16px
    full: '9999px',  // Fully rounded
  },

  // Shadows
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  },

  // Component-specific styles
  components: {
    header: {
      height: '60px',
      padding: '12px 16px',
    },
    messageBox: {
      maxWidth: '80%',
      padding: '12px 16px',
      borderRadius: '12px',
    },
    starterQuestion: {
      padding: '10px 20px',
      borderRadius: '24px',
      fontSize: '0.9375rem', // 15px
    },
    input: {
      height: '48px',
      borderRadius: '24px',
      padding: '12px 20px',
    },
  },
};

// Helper function to get SVG background pattern
export const getBackgroundPattern = (color = '#6B9F4B') => {
  // Light decorative pattern with low opacity
  return `
    <svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100">
      <defs>
        <pattern id="pattern" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
          <circle cx="20" cy="20" r="15" fill="${color}" opacity="0.03"/>
          <circle cx="80" cy="80" r="12" fill="${color}" opacity="0.04"/>
          <circle cx="50" cy="60" r="8" fill="${color}" opacity="0.02"/>
          <circle cx="70" cy="30" r="10" fill="${color}" opacity="0.03"/>
          <path d="M30,40 Q40,45 50,40" stroke="${color}" stroke-width="1.5" fill="none" opacity="0.04"/>
          <path d="M60,70 Q70,75 80,70" stroke="${color}" stroke-width="1.5" fill="none" opacity="0.03"/>
        </pattern>
      </defs>
      <rect width="100" height="100" fill="url(#pattern)"/>
    </svg>
  `.trim();
};

// Convert SVG to data URI for use in CSS
export const getBackgroundDataURI = (color) => {
  const svg = getBackgroundPattern(color);
  // Use encodeURIComponent instead of btoa to handle all characters
  const encoded = encodeURIComponent(svg);
  return `url("data:image/svg+xml,${encoded}")`;
};
